/**
 * Chart theme utilities that use CSS custom properties from index.css
 * This ensures charts automatically adapt when the theme changes
 */

/**
 * Get a CSS custom property value from the document root
 */
export const getCSSVar = (varName: string): string => {
	if (typeof window === "undefined") return "";
	return getComputedStyle(document.documentElement)
		.getPropertyValue(`--color-${varName}`)
		.trim();
};

/**
 * Check if dark mode is currently active
 */
const isDarkMode = (theme?: "light" | "dark"): boolean => {
	// If theme is explicitly provided, use it directly
	if (theme) {
		return theme === "dark";
	}

	if (typeof window === "undefined") return false;
	return document.documentElement.classList.contains("dark");
};

/**
 * Get chart colors from theme
 * @param theme - Optional theme value from context. If provided, uses this instead of auto-detection
 */
export const getChartColors = (theme?: "light" | "dark") => ({
	// Theme-aware text colors for chart labels
	text: {
		primary: isDarkMode(theme)
			? getCSSVar("white") || "#ffffff"
			: getCSSVar("gray-900"),
		secondary: isDarkMode(theme)
			? getCSSVar("gray-400")
			: getCSSVar("gray-500"),
		label: isDarkMode(theme)
			? getCSSVar("white") || "#ffffff"
			: getCSSVar("gray-900"),
	},
	// Brand colors (primary)
	brand: {
		primary: getCSSVar("brand-500"),
		light: getCSSVar("brand-300"),
		lighter: getCSSVar("brand-200"),
		lightest: getCSSVar("brand-100"),
		dark: getCSSVar("brand-700"),
	},
	// Semantic colors
	success: {
		primary: getCSSVar("success-500"),
		light: getCSSVar("success-300"),
		bg: getCSSVar("success-50"),
	},
	error: {
		primary: getCSSVar("error-500"),
		light: getCSSVar("error-300"),
		bg: getCSSVar("error-50"),
	},
	warning: {
		primary: getCSSVar("warning-500"),
		light: getCSSVar("warning-300"),
		bg: getCSSVar("warning-50"),
	},
	// Neutral colors
	gray: {
		50: getCSSVar("gray-50"),
		100: getCSSVar("gray-100"),
		200: getCSSVar("gray-200"),
		300: getCSSVar("gray-300"),
		400: getCSSVar("gray-400"),
		500: getCSSVar("gray-500"),
		600: getCSSVar("gray-600"),
		700: getCSSVar("gray-700"),
		800: getCSSVar("gray-800"),
		900: getCSSVar("gray-900"),
	},
	// Blue light colors
	blueLight: {
		primary: getCSSVar("blue-light-500"),
		light: getCSSVar("blue-light-300"),
	},
});

/**
 * Predefined color palettes for charts
 */
export const chartPalettes = {
	// Primary palette using brand colors
	primary: [
		getCSSVar("brand-500"),
		getCSSVar("brand-300"),
		getCSSVar("blue-light-500"),
		getCSSVar("theme-purple-500"),
	],
	// Comparison palette for property vs rental income
	comparison: [getCSSVar("brand-500"), getCSSVar("brand-300")],
	// Multi-series palette
	multiSeries: [
		getCSSVar("brand-500"),
		getCSSVar("brand-300"),
		getCSSVar("blue-light-500"),
		getCSSVar("success-500"),
		getCSSVar("warning-500"),
	],
};

/**
 * Common chart styling configuration for Recharts
 */
export const commonChartConfig = {
	cartesianGrid: {
		strokeDasharray: "3 3",
		stroke: getCSSVar("gray-200"),
		vertical: false,
	},
	xAxis: {
		tick: { fill: getCSSVar("gray-500"), fontSize: 12 },
		axisLine: { stroke: getCSSVar("gray-200") },
		tickLine: false,
	},
	yAxis: {
		tick: { fill: getCSSVar("gray-500"), fontSize: 12 },
		axisLine: false,
		tickLine: false,
	},
	tooltip: {
		contentStyle: {
			backgroundColor: "white",
			border: `1px solid ${getCSSVar("gray-200")}`,
			borderRadius: "8px",
			boxShadow: "0px 4px 8px -2px rgba(16, 24, 40, 0.1)",
			fontSize: 12,
		},
		labelStyle: {
			color: getCSSVar("gray-800"),
			fontWeight: 500,
		},
	},
};
